import React, { useState, useEffect } from 'react';
import { Shield, Users, Scale, Edit, FileText, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { complianceAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';
import { RichTextEditor } from '../ui/rich-text-editor';
import { DocumentsHealthCard } from './DocumentsHealthCard';

const compliancePages = [
  { 
    type: 'privacy', 
    title: 'Politica Privacy', 
    icon: Shield,
    description: 'Gestisci la pagina della politica sulla privacy'
  },
  { 
    type: 'dpo', 
    title: 'Responsabile Protezione Dati', 
    icon: Users,
    description: 'Gestisci le informazioni sul DPO'
  },
  { 
    type: 'gender-equality', 
    title: 'Parità di Genere', 
    icon: Scale,
    description: 'Gestisci la pagina sulla parità di genere'
  },
  {
    type: 'whistleblowing-info',
    title: 'Whistleblowing: cos\'è',
    icon: Info,
    description: 'Testo informativo mostrato in cima al modulo di segnalazione'
  },
  {
    type: 'whistleblowing',
    title: 'Informativa Whistleblowing',
    icon: Shield,
    description: 'Informativa privacy del canale di segnalazione'
  },
  {
    type: 'codice-etico',
    title: 'Codice Etico',
    icon: Scale,
    description: 'Pubblica testo e PDF del Codice Etico'
  },
  {
    type: 'modello-231',
    title: 'Modello 231',
    icon: FileText,
    description: 'Pubblica testo e PDF del Modello 231'
  }
];

const ComplianceTab = () => {
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageForm, setPageForm] = useState({ title: '', content: '', pdf_url: '' });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await complianceAPI.getAll();
      const pagesMap = {};
      response.data.forEach(page => {
        pagesMap[page.page_type] = page;
      });
      setPages(pagesMap);
    } catch (error) {
      console.error('Error loading compliance pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pageConfig) => {
    const existingPage = pages[pageConfig.type];
    setEditingPage(pageConfig);
    setPageForm({
      title: existingPage?.title || pageConfig.title,
      content: existingPage?.content || '',
      pdf_url: existingPage?.pdf_url || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await complianceAPI.update(editingPage.type, {
        page_type: editingPage.type,
        title: pageForm.title,
        content: pageForm.content,
        pdf_url: pageForm.pdf_url || null
      });
      toast.success('Pagina aggiornata con successo!');
      loadPages();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Errore nel salvare la pagina');
    }
  };

  return (
    <div className="space-y-6">
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="text-2xl">Gestione Compliance</CardTitle>
        <CardDescription>Modifica i contenuti delle pagine di compliance</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">Caricamento...</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {compliancePages.map((pageConfig) => {
              const Icon = pageConfig.icon;
              const existingPage = pages[pageConfig.type];
              
              return (
                <Card key={pageConfig.type} className="border border-border" data-testid={`compliance-card-${pageConfig.type}`}>
                  <CardContent className="pt-6">
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-lg text-foreground">
                          {pageConfig.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {pageConfig.description}
                        </p>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        {existingPage?.pdf_url && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <FileText className="w-4 h-4 mr-1" />
                            PDF allegato
                          </div>
                        )}
                      </div>
                      <Button 
                        onClick={() => handleEdit(pageConfig)}
                        className="w-full"
                        data-testid={`edit-compliance-${pageConfig.type}`}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        {existingPage ? 'Modifica' : 'Configura'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifica {editingPage?.title}</DialogTitle>
              <DialogDescription>Aggiorna il contenuto della pagina</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Titolo Pagina</Label>
                <Input
                  id="title"
                  value={pageForm.title}
                  onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })}
                  required
                  className="mt-2"
                  data-testid="compliance-title-input"
                />
              </div>
              <div>
                <Label htmlFor="content">Contenuto</Label>
                <div className="mt-2">
                  <RichTextEditor
                    value={pageForm.content}
                    onChange={(value) => setPageForm({ ...pageForm, content: value })}
                    placeholder="Inserisci il contenuto della pagina..."
                    minHeight="300px"
                  />
                </div>
              </div>
              <div>
                <Label>Documento PDF (opzionale)</Label>
                <div className="mt-2">
                  <ImageUpload
                    value={pageForm.pdf_url}
                    onChange={(url) => setPageForm({ ...pageForm, pdf_url: url })}
                    uploadType="document"
                    label="Carica documento PDF"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="submit-compliance-btn">
                  Salva Modifiche
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
      <DocumentsHealthCard />
    </div>
  );
};

export default ComplianceTab;
